<template>
  <div class="flex flex-col h-full">
    <div
      class="flex items-center gap-4 border-b border-gray-200 px-4 py-3 dark:border-dark-3"
    >
      <div class="flex-1">
        <div class="text-xs text-gray-500 dark:text-gray-400">Gefunden</div>
        <div class="text-lg font-medium">
          {{ matchedCount }} / {{ filterCount }}
        </div>
      </div>
      <div class="flex-1">
        <div class="text-xs text-gray-500 dark:text-gray-400">
          Geräte gelesen
        </div>
        <div class="text-lg font-medium">{{ totalCount }}</div>
      </div>
      <div
        class="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400"
      >
        <em class="mdi mdi-loading animate-spin text-xs" />
        <span>{{ formattedTime }}</span>
      </div>
    </div>

    <div class="flex border-b border-gray-200 dark:border-dark-3">
      <button
        class="flex-1 py-2 text-center text-sm font-medium transition-colors"
        :class="
          activeTab === 'filter'
            ? 'border-b-2 border-blue-500 text-blue-500'
            : 'text-gray-500'
        "
        @click="activeTab = 'filter'"
      >
        Filterliste
      </button>
      <button
        class="flex-1 py-2 text-center text-sm font-medium transition-colors"
        :class="
          activeTab === 'all'
            ? 'border-b-2 border-blue-500 text-blue-500'
            : 'text-gray-500'
        "
        @click="activeTab = 'all'"
      >
        Alle Geräte
      </button>
    </div>

    <div class="flex-1 overflow-auto">
      <div v-if="activeTab === 'filter'">
        <QToolDeviceItem
          v-for="device in filterDevices"
          :key="device.serialNumber"
          :serial-number="device.serialNumber"
          :type="device.type"
          :rssi="device.rssi"
          :is-found="device.isFound"
        />
        <div
          v-if="filterDevices.length === 0"
          class="p-6 text-center text-gray-400"
        >
          Keine Geräte in der Filterliste
        </div>
      </div>
      <div v-else>
        <QToolDeviceItem
          v-for="device in readoutDevices"
          :key="device.serialNumber"
          :serial-number="device.serialNumber"
          :type="device.type"
          :rssi="device.rssi"
          :is-found="true"
        />
        <div
          v-if="readoutDevices.length === 0"
          class="flex flex-col items-center gap-2 p-6 text-gray-400"
        >
          <em class="mdi mdi-loading animate-spin text-2xl" />
          <span>Warte auf Geräte...</span>
        </div>
      </div>
    </div>

    <div v-if="readoutState === 'error'" class="p-4 text-center">
      <p class="text-red-500 mb-2">Verbindung verloren</p>
      <button
        class="rounded-lg bg-blue-500 px-4 py-2 text-white"
        @click="$emit('rePair')"
      >
        Q-Tool erneut verbinden
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import type { MatchedDevice } from '@/composables/useQTool';
import type {
  ReadoutDeviceResult,
  QToolReadoutState,
} from '@/native/plugins/QTool';
import QToolDeviceItem from '@/components/QTool/QToolDeviceItem.vue';

defineProps<{
  filterDevices: MatchedDevice[];
  readoutDevices: ReadoutDeviceResult[];
  matchedCount: number;
  filterCount: number;
  totalCount: number;
  readoutState: QToolReadoutState;
}>();

defineEmits<{
  rePair: [];
}>();

const activeTab = ref<'filter' | 'all'>('filter');
const elapsedSeconds = ref(0);
const formattedTime = ref('00:00');
let timerInterval: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  timerInterval = setInterval(() => {
    elapsedSeconds.value++;
    const minutes = Math.floor(elapsedSeconds.value / 60);
    const seconds = elapsedSeconds.value % 60;
    formattedTime.value = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }, 1000);
});

onUnmounted(() => {
  if (timerInterval) clearInterval(timerInterval);
});
</script>
